import { _js } from '@ifixit/localize';
import { memo, Fragment, ComponentProps, ReactNode } from 'react';
import DiagramTooltip from '../Generic/diagramTooltip';
import FloatingPartsList from '../MiscParts/floating_parts_list';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { useSearchContext } from 'Guide/Schematics/Diagram/Providers/searchContext';
import { useDataContext } from 'Guide/Schematics/Diagram/Providers/dataContext';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';

type HeaderWrapperProps = ComponentProps<'div'>;

export function HeaderWrapper({ className, ...props }: HeaderWrapperProps) {
   return (
      <div
         className={cn('flex bg-gray-50 justify-between items-center py-3 px-4', className)}
         {...props}
      >
         {props.children}
      </div>
   );
}

function HeaderContainer({ className, ...props }: HeaderWrapperProps) {
   return (
      <HeaderWrapper
         className={cn(
            'fixed top-0 left-0 right-0 mx-8 mt-4 shadow-2xl rounded-lg z-[9999]',
            className
         )}
         {...props}
      >
         {props.children}
      </HeaderWrapper>
   );
}

function PartsListIcon() {
   return <FaIcon name="list" style="regular" className="text-sm text-gray-800 align-middle" />;
}

function ExitIcon() {
   return <FaIcon name="xmark" style="regular" className="text-sm text-gray-800 align-middle" />;
}

type HeaderButtonProps = ComponentProps<'button'>;

function HeaderButton({ children, className, ...props }: HeaderButtonProps) {
   return (
      <button
         className={cn(
            'bg-gray-50 border border-gray-300 rounded-md font-bold text-center text-sm',
            'py-2.5 px-2.5 md:px-4',
            'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300',
            className
         )}
         {...props}
      >
         {children}
      </button>
   );
}

type PartsListButtonProps = HeaderButtonProps & {
   icon?: ReactNode;
};

function PartsListButton({ children, icon, ...props }: PartsListButtonProps) {
   return (
      <>
         {/* Mobile: icon only */}
         <HeaderButton className="md:hidden" {...props}>
            {icon}
         </HeaderButton>
         {/* Desktop: text with icon */}
         <HeaderButton className="hidden md:flex items-center gap-2" {...props}>
            {children}
            {icon}
         </HeaderButton>
      </>
   );
}

type ExitButtonProps = HeaderButtonProps;

export function ExitButton({ children, ...props }: ExitButtonProps) {
   return (
      <>
         {/* Mobile: icon only */}
         <HeaderButton className="md:hidden" {...props}>
            <ExitIcon />
         </HeaderButton>
         {/* Desktop: text */}
         <HeaderButton className="hidden md:block!" {...props}>
            {children}
         </HeaderButton>
      </>
   );
}

type TitleProps = ComponentProps<'span'>;

function Title({ className, ...props }: TitleProps) {
   return (
      <span
         className={cn(
            'font-bold text-sm md:text-lg text-center text-black p-2 truncate whitespace-nowrap focus:outline-none',
            className
         )}
         {...props}
      >
         {props.children}
      </span>
   );
}

type SubTitleProps = ComponentProps<'span'>;

function SubTitle({ className, ...props }: SubTitleProps) {
   return (
      <span className={cn('text-xs text-center text-gray-600', className)} {...props}>
         {props.children}
      </span>
   );
}

type TitleWithTooltipProps = {
   title: string;
   workOrder?: string;
} & ComponentProps<'div'>;

export const TitleWithTooltip = memo(function TitleWithTooltipInternal({
   title,
   workOrder,
   className,
   ...props
}: TitleWithTooltipProps) {
   const content = (
      <Fragment>
         {title}
         {workOrder && <SubTitle>{_js('Work Order: %1', workOrder)}</SubTitle>}
      </Fragment>
   );

   return (
      <DiagramTooltip content={content}>
         <div className={cn('line-clamp-1', className)} {...props}>
            <Title className="p-0">{title}</Title>
            {workOrder && <SubTitle>{_js('Work Order: %1', workOrder)}</SubTitle>}
         </div>
      </DiagramTooltip>
   );
});

function Header({ onClose, title }: { title: string } & Pick<MinimalDisclosure, 'onClose'>) {
   const searchContext = useSearchContext<iFixitHotSpotItem>();
   const dataContext = useDataContext<iFixitHotSpotItem>();
   const partsListCount = dataContext.data.length;
   const { onToggle } = searchContext.disclosure;

   return (
      <HeaderContainer>
         <ExitButton onClick={onClose} aria-label="Close Diagram Viewer">
            {_js('Exit Viewer')}
         </ExitButton>
         <TitleWithTooltip title={title} />
         <PartsListButton onClick={onToggle} icon={<PartsListIcon />} aria-label="Show Parts List">
            {_js('Show Parts List (%1)', partsListCount.toString())}
         </PartsListButton>
         <FloatingPartsList />
      </HeaderContainer>
   );
}
export default memo(Header);
